import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader2, Crown, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const PaymentResult = ({ status }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [countdown, setCountdown] = useState(8);

  useEffect(() => {
    const verifyPayment = async () => {
      const externalReference = searchParams.get('external_reference');
      if (externalReference) {
        try {
          const response = await api.get(`/subscription/verify/${externalReference}`);
          if (response.data.status === 'active') {
            setSubscriptionActive(true);
          }
        } catch (error) {
          console.error('Error verifying subscription:', error);
        }
      }
      setVerifying(false);
    };
    setTimeout(verifyPayment, 2000);
  }, [searchParams]);

  // Auto-redirect to home after success (8 seconds)
  useEffect(() => {
    if (verifying || status !== 'success') return;
    if (countdown <= 0) {
      navigate('/');
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, verifying, status, navigate]);

  const getStatusContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircle className="w-24 h-24 text-green-500" />,
          title: '¡Pago Exitoso! 🎉',
          message: subscriptionActive
            ? '¡Bienvenido a U-CAN Premium! Tu suscripción está activa y ya puedes disfrutar de todos los beneficios.'
            : 'Tu pago se está procesando. En unos minutos tu suscripción estará 100% activa.',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-300',
          accentColor: 'text-green-700'
        };
      case 'failure':
        return {
          icon: <XCircle className="w-24 h-24 text-red-500" />,
          title: 'Pago Rechazado',
          message: 'No pudimos procesar tu pago. Por favor intenta nuevamente con otro método de pago o contáctanos a hola@u-can.cl.',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-300',
          accentColor: 'text-red-700'
        };
      case 'pending':
        return {
          icon: <Clock className="w-24 h-24 text-yellow-500" />,
          title: 'Pago Pendiente',
          message: 'Tu pago está siendo procesado. Te notificaremos por email cuando se confirme.',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-300',
          accentColor: 'text-yellow-700'
        };
      default:
        return {
          icon: <Clock className="w-24 h-24 text-gray-400" />,
          title: 'Estado Desconocido',
          message: 'No pudimos determinar el estado de tu pago. Contáctanos a hola@u-can.cl si tienes dudas.',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-300',
          accentColor: 'text-gray-700'
        };
    }
  };

  const content = getStatusContent();

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#E6202E] animate-spin mx-auto mb-4" />
          <p className="text-gray-700 text-lg font-medium">Verificando tu pago...</p>
          <p className="text-gray-500 text-sm mt-2">Esto puede tomar unos segundos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white p-4">
      <div className={`max-w-md w-full ${content.bgColor} rounded-3xl p-8 border-2 ${content.borderColor} text-center shadow-xl`} data-testid={`payment-${status}`}>
        {status === 'success' && (
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full p-3 shadow-lg animate-bounce">
              <Crown className="w-8 h-8 text-white" />
            </div>
          </div>
        )}

        <div className="flex justify-center mb-6 animate-pulse">
          {content.icon}
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {content.title}
        </h1>

        <p className="text-gray-700 mb-6 text-base leading-relaxed">
          {content.message}
        </p>

        {status === 'success' && subscriptionActive && (
          <div className="bg-white rounded-xl p-4 mb-6 border border-green-200">
            <p className="text-sm font-bold text-green-800 mb-2 flex items-center justify-center gap-1">
              <Crown className="w-4 h-4" /> Cuenta Premium Activa
            </p>
            <p className="text-xs text-gray-600">Acceso completo a todos los beneficios</p>
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={() => navigate('/')}
            className="w-full bg-[#E6202E] hover:bg-[#D31522] text-white py-6 text-lg font-bold"
            data-testid="back-to-home-btn"
          >
            <Home className="w-5 h-5 mr-2" />
            Volver al Inicio
            {status === 'success' && countdown > 0 && (
              <span className="ml-2 text-sm opacity-80">({countdown}s)</span>
            )}
          </Button>

          {status === 'failure' && (
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full border-gray-300 py-5"
            >
              Intentar de Nuevo
            </Button>
          )}

          {status === 'pending' && (
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full border-gray-300 py-5"
            >
              Ir a Mi Cuenta
            </Button>
          )}
        </div>

        {status === 'success' && (
          <p className="text-xs text-gray-500 mt-6">
            Te redirigiremos automáticamente al inicio en {countdown} segundos
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;